import React, { FC, useContext, useEffect, useReducer, useState } from 'react'
import styled from 'styled-components'
import { PoolWithData, TokenWithData } from '../../../api/api-types'
import { Wei } from '../../../utils/types'
import { Web3Context } from '../../../hooks/web3'
import { ViewState } from '../deposit/DepositState'
import { SwapStyle } from '../../Themes'
import { EmptyLine } from '../../catalog/EmptyLine'
import { MultiDepositPreview } from '../deposit/MultiDepositPreview'
import { SwaapLargeLoader } from '../../catalog/SwaapLoader'
import { ExitApi, WithdrawResult } from '../../../api/web3/exit-api'
import { FullPoolLine } from '../../../logic/pools/process-pools'
import { floorWei, fromWei } from '../../../utils/static-web3'
import { FailWithdrawPopup } from './FailWithdrawPopup'
import { WithdrawStates } from './WithdrawState'
import { WithdrawPostview } from './WithdrawPostview'
import SwapButton from '../../swap/SwapButton'
import { maxWithdrawSlippage } from '../../../config'
import { WithdrawInput } from './WithdrawInput'

import CurrencyLogo from '../../CurrencyLogo'
import { coinImage } from '../../../assets/coins/coins'
import { priceFormatter } from '../../../logic/financials/precise'
import {
  extractErrorMessage,
  sendIllegalStateException,
} from '../../../logic/errors/extract-error-message'
import { useTransactions } from '../../transactions/TransactionContext'
import { ArrowBackFixed } from '../../catalog/ArrowDownFixed'
import { PopupStyleWrapper, VerticalBox } from '../deposit/DepositPopup'
import { createEarnTabEvent, createSuccessEvent } from 'user-events-segment'
import { Amounts } from '../../../logic/token/amounts'
import { Pool } from '../../../logic/pools/Pool'
import { oracleApi } from '../../../api/oracle/oracle-client'
import { withdrawReducer } from './withdraw-reducer'

const WithdrawPopupWrapper = styled.div`
  font-size: 1.6rem;
  display: flex;
  justify-content: center;

  section {
    width: ${SwapStyle.popupWidth};
  }
`

export interface Props {
  line: FullPoolLine
  pool: PoolWithData
  onWithdraw: (
    userShareExit: Wei,
    amounts: Map<TokenWithData, Wei>,
    all: boolean
  ) => void
  onExit: VoidFunction
}

export const WithdrawPopup: FC<Props> = ({
  line,
  pool,
  onExit,
  onWithdraw,
}) => {
  const tokens: TokenWithData[] = pool.tokens
  const { web3, account, appChainID } = useContext(Web3Context)
  const [amounts, setAmounts] = useState<Map<TokenWithData, Wei | undefined>>(
    new Map()
  )
  const [valid, setValid] = useState(false)
  const [percent, setPercent] = useState<number | undefined>(undefined)

  const [currentWithdraw, withdrawDispatcher] = useReducer(withdrawReducer, {
    pool,
  })

  const {
    CONNECT,
    CHOOSE_AMOUNT,
    VALID,
    PREVIEW,
    PROCESSING,
    TRANSACTION_SUCCESS,
    TRANSACTION_FAILED,
  } = WithdrawStates
  const [state, setState] = useState<ViewState>(CHOOSE_AMOUNT)
  const [buttonMessage, setButtonMessage] = useState<string>(getMessage(state))

  useEffect(() => {
    reset()
    withdrawDispatcher({ type: 'choose-pool', pool })
  }, [])

  useEffect(() => {
    rebalance()
  }, [percent])

  useEffect(() => {
    setState(valid ? VALID : CHOOSE_AMOUNT)
  }, [valid])

  function checkValid() {
    if (percent === undefined) {
      return false
    }
    setValid(percent > 0 && percent <= 100)
  }

  function isValidPercent() {
    return percent !== undefined && percent > 0 && percent <= 100
  }

  async function rebalance() {
    if (!isValidPercent()) {
      setValid(false)
      setState(CHOOSE_AMOUNT)
      return
    }

    const exitApi = new ExitApi(web3!, account!, appChainID, line.pool.address)

    const exitAmount = getExitAmount()
    try {
      const _amounts = await exitApi.viewMultiAssetExitShare(exitAmount)
      for (let i = 0; i < tokens.length; i++) {
        const token = tokens[i]
        const amountWei = _amounts[i]
        amounts.set(token, amountWei)
      }
      setAmounts(new Map(amounts))
      checkValid()
    } catch (error: any) {
      setAmounts(new Map())
      console.error(extractErrorMessage(error))
      setState(CHOOSE_AMOUNT.withError(extractErrorMessage(error)))
      setButtonMessage(extractErrorMessage(error))
      setValid(false)
      // event of error
      createEarnTabEvent(pool).percentage_withdraw_fail(
        percent!,
        extractErrorMessage(error)
      )
    }
  }

  function getMessage(state: ViewState) {
    switch (state.key) {
      case CONNECT.key:
        return 'Connect'
      case CHOOSE_AMOUNT.key:
        return 'Choose amount'
      case VALID.key:
        return 'Preview'

      case PROCESSING.key:
        return 'Processing transaction'
      case TRANSACTION_SUCCESS.key:
      case TRANSACTION_FAILED.key:
        return 'OK'
    }
    return 'Withdraw'
  }

  function getExitAmount(): Wei {
    // that's the percent of his share the user exits. Ex 5btc out of 50 he deposit => 10%
    const userSharesWei = line.userShares
    if (userSharesWei === undefined) {
      sendIllegalStateException(
        'User has no share but wants to exit',
        WithdrawPopup
      )
      return '0'
    }
    if (percent === undefined) {
      return '0'
    }

    // Number of SwaapLP tokens the user exits.
    if (percent === 100) {
      return userSharesWei
    } else {
      return floorWei((Number(userSharesWei) * percent) / 100)
    }
  }

  const { dispatcher } = useTransactions()

  async function executeWithdraw(): Promise<WithdrawResult> {
    // verification on max
    const exitAmount = getExitAmount()
    const exitApi = new ExitApi(web3!, account!, appChainID, line.pool.address)
    return await exitApi.exit(
      line.pool,
      line.tokens,
      exitAmount,
      amounts as Map<TokenWithData, Wei>,
      dispatcher
    )
  }

  function reset() {
    const newAmounts = new Map<TokenWithData, Wei>()
    for (const token of tokens) {
      newAmounts.set(token, '0')
    }
    setAmounts(newAmounts)

    setPercent(undefined)
  }

  async function handleClick() {
    if (state === PREVIEW) {
      try {
        setState(PROCESSING)
        createEarnTabEvent(pool).clicked_confirmed_withdraw()
        const withdrawResult = await executeWithdraw()
        onWithdraw(
          withdrawResult!.userShareExit!,
          withdrawResult!.amounts!,
          percent === 100
        )
        fireWithdrawEvent(pool, withdrawResult, percent!)

        setState(TRANSACTION_SUCCESS.withSubState({ ...withdrawResult }))
      } catch (error: any) {
        console.error(extractErrorMessage(error))
        setState(TRANSACTION_FAILED.withError(extractErrorMessage(error)))
        let hash = error.hash
        const step = error.hash ? 'revert' : 'not_accepted'
        const reason = extractErrorMessage(error)
        withdrawDispatcher({
          type: 'failure',
          data: {
            step,
            reason,
            percent,
            amounts: Amounts.fromMap(amounts),
            expected_burnt: getExitAmount(),
            hash,
          },
        })
      }
    } else if (state === TRANSACTION_SUCCESS || state === TRANSACTION_FAILED) {
      onExit()
      reset()
    } else {
      if (state === VALID) {
        createEarnTabEvent(pool).clicked_preview_withdraw(
          fromWei(getExitAmount(), 18),
          Amounts.fromMap(amounts).toAnalyticObject()
        )
      }
      setState(state.next())
    }
  }

  function enableButton() {
    if (state.isBefore(VALID)) {
      return valid
    } else {
      return state.isIn([PREVIEW, TRANSACTION_SUCCESS, TRANSACTION_FAILED])
    }
  }

  return (
    <WithdrawPopupWrapper>
      <PopupStyleWrapper className={'withdraw-wrapper'}>
        <VerticalBox className={'withdraw-vertical-box'}>
          <section>
            <EmptyLine />
            {state.isIn([CONNECT, CHOOSE_AMOUNT, VALID]) && (
              <div>
                <WithdrawInput
                  onPercentChange={percent => {
                    setPercent(percent)
                  }}
                  percent={isValidPercent() ? percent : undefined}
                />
                <div className="light mt20">
                  Estimated output{amounts.size > 1 ? 's' : ''}:
                </div>
                <div
                  className="mt10"
                  style={{ alignSelf: 'center', minWidth: '100%' }}
                >
                  {Array.from(amounts.keys()).map((token, idx) => (
                    <ReceivedLine
                      key={token.symbol}
                      className={idx > 0 ? 'pt10' : ''}
                    >
                      <div className={'align-center'}>
                        <CurrencyLogo src={coinImage(token.symbol)!} />
                        <ReceivedToken>{token.symbol}</ReceivedToken>
                      </div>
                      <ReceivedQuantity>
                        <span className={'mr20'}>
                          {priceFormatter(
                            fromWei(amounts.get(token)!, token.decimals)
                          )}
                        </span>
                      </ReceivedQuantity>
                    </ReceivedLine>
                  ))}
                </div>
              </div>
            )}
            {state === PREVIEW && (
              <div className={'vertical'}>
                <Back onClick={() => setState(VALID)}>
                  <ArrowBackFixed /> <span className={'ml10'}>Back</span>
                </Back>

                <MultiDepositPreview
                  amounts={amounts as Map<TokenWithData, Wei>}
                  pool={pool}
                  expectedShare={getExitAmount()}
                  tokensSlippage={maxWithdrawSlippage}
                  isWithdraw={true}
                />
              </div>
            )}
            {state === PROCESSING && (
              <>
                <SwaapLargeLoader />
              </>
            )}
            {state === TRANSACTION_SUCCESS && (
              <WithdrawPostview
                userShareExit={state.subState!.userShareExit!}
                amounts={state.subState!.amounts!}
                pool={pool}
                hash={state.subState!.transactionHash!}
              />
            )}
            {state === TRANSACTION_FAILED && (
              <FailWithdrawPopup state={state} onExit={onExit} />
            )}
            <EmptyLine />
            <div className={'justify-center'}>
              <SwapButton
                handleOnClick={() => handleClick()}
                message={getMessage(state)}
                isReadyToSwap={enableButton()}
              />
            </div>
          </section>
        </VerticalBox>
      </PopupStyleWrapper>
    </WithdrawPopupWrapper>
  )
}

export const ReceivedLine = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: 2rem;
  min-width: 300px;
  align-items: center;
  height: 40px;
`

const ReceivedQuantity = styled.span`
  text-align: left;
  font-family: Montserrat-Medium, Montserrat;
  font-size: 2.25rem;
  align-items: center;
  display: flex;
`

export const ReceivedToken = styled.span`
  text-align: left;
  align-items: center;
  display: flex;
  font-size: 1.5rem;
`

const Back = styled.div`
  cursor: pointer;
  display: flex;
  margin-bottom: 20px;
`

function fireWithdrawEvent(
  pool: Pool,
  result: WithdrawResult,
  percent: number
) {
  oracleApi.getPrices().then(() => {
    let amounts = Amounts.fromMap(result.amounts)
    createSuccessEvent().withdraw_success(
      result.transactionHash,
      pool.address,
      pool.tokens.map(t => t.symbol),
      amounts.toAnalyticObject(),
      fromWei(result.userShareExit, 18),
      oracleApi.calculateBag(amounts),
      percent
    )
  })
}
